import { Inbox } from "lucide-react";

import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { shortenAddress } from "../../utils/cryptoSwapAddress";
import { MonoValue } from "../shared/CryptoSwapBits";
import { OrderAmounts } from "../shared/OrderAmounts";
import { CryptoSwapStatusPill } from "./CryptoSwapStatusPill";

export interface CryptoSwapOrdersCardsProps {
  rows: OtcOrder[];
  isLoading: boolean;
  isFiltered: boolean;
  onOpen: (order: OtcOrder) => void;
}

/** The same history below `lg`: one card per order instead of a table squeezed into 390 pixels. */
export function CryptoSwapOrdersCards({ rows, isLoading, isFiltered, onOpen }: CryptoSwapOrdersCardsProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2.5">
        {Array.from({ length: 4 }, (_, index) => (
          <ColocaSkeleton key={index} className="h-24 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <ColocaEmpty
        icon={Inbox}
        title={isFiltered ? t("table_no_results_title") : t("table_empty_title")}
        description={isFiltered ? t("table_no_results_description") : t("table_empty_description")}
      />
    );
  }

  return (
    <ul className="flex list-none flex-col gap-2.5 p-0">
      {rows.map((order) => (
        <li key={order.id}>
          <button
            type="button"
            onClick={() => onOpen(order)}
            className={cn(
              colocaSurfaceVariants({ variant: "panel", padding: "md", interactive: true }),
              "flex w-full flex-col gap-2.5",
            )}
          >
            <span className="flex flex-wrap items-center justify-between gap-2">
              <MonoValue className="text-xs text-muted-foreground">
                {formatDateTime(order.created_at, { locale: activeLanguage })}
              </MonoValue>
              <CryptoSwapStatusPill status={order.status} size="sm" />
            </span>
            <OrderAmounts order={order} size="sm" />
            <MonoValue className="text-xs text-muted-foreground">
              {shortenAddress(order.destination.address) || "—"}
            </MonoValue>
          </button>
        </li>
      ))}
    </ul>
  );
}
